import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, minsToHM } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { KPICard, EmptyState, Spinner, SectionTitle } from '@/components/common';
import { toast } from 'sonner';
import { Clock, Play, Square, Sparkles, Trash2, Pause } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { cn } from '@/lib/utils';

const fmtElapsed = (secs) => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// Chart config hoisted to module scope so child charts don't re-render from fresh object refs
const CHART_MARGIN = { top: 4, right: 8, left: -20, bottom: 0 };
const AXIS_TICK = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' };
const TOOLTIP_STYLE = { background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 };
const BAR_RADIUS = [3, 3, 0, 0];

export default function TimeTracker() {
  const { activeSession, elapsed, isPaused, clockIn, clockOut, pauseSession, resumeSession, stagesData } = useApp();
  const [summary, setSummary] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState('Product Research');
  const [productId, setProductId] = useState('none');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [fbBusy, setFbBusy] = useState(false);

  const load = useCallback(async () => {
    const [s, sess, p] = await Promise.all([api.get('/time/summary'), api.get('/time/sessions?days=14'), api.get('/products')]);
    setSummary(s.data);
    setSessions(sess.data);
    setProducts(p.data);
  }, []);

  useEffect(() => { load(); }, [load, activeSession]);

  const handleClockIn = async () => {
    setBusy(true);
    try {
      await clockIn(category, productId === 'none' ? null : productId, notes);
      toast.success(`Clocked in — ${category}`);
      setNotes('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clock-in failed');
    } finally { setBusy(false); }
  };

  const handleClockOut = async () => {
    setBusy(true);
    try {
      const s = await clockOut(notes);
      toast.success(`Session logged: ${s.duration_minutes} min of ${s.category}`);
      setNotes('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clock-out failed');
    } finally { setBusy(false); }
  };

  const handlePauseResume = async () => {
    setBusy(true);
    try {
      if (isPaused) {
        await resumeSession();
        toast.success('Resumed — timer running');
      } else {
        await pauseSession();
        toast.info("Paused — this time won't count toward your hours");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const getFeedback = async () => {
    setFbBusy(true);
    try {
      const { data } = await api.post('/ai/time-feedback');
      setFeedback(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI feedback failed');
    } finally { setFbBusy(false); }
  };

  const delSession = async (s) => {
    await api.delete(`/time/sessions/${s.id}`);
    load();
  };

  const chartData = useMemo(
    () => (summary?.week_days || []).map((d) => ({ ...d, label: d.date.slice(5), hours: Math.round((d.minutes / 60) * 10) / 10 })),
    [summary]);

  const categoryWeek = useMemo(
    () => Object.entries(summary?.by_category_week || {}).sort((a, b) => b[1] - a[1]),
    [summary]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Time Tracker</h1>
        <div className="page-accent-bar" />
        <p className="mt-2 text-sm text-muted-foreground">Clock in, do the work, clock out. Consistency is the moat. Pauses don't count toward your hours.</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardContent className="p-6">
            {activeSession ? (
              <div className="flex flex-col items-center gap-4 text-center">
                <div className="flex items-center gap-2">
                  <p className="text-sm text-muted-foreground">{isPaused ? 'Session paused' : 'Currently working on'}</p>
                  {isPaused && <span data-testid="time-tracker-paused-badge" className="rounded bg-amber-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-300">Paused</span>}
                </div>
                <p className="text-lg font-semibold">{activeSession.category}{activeSession.product_name ? ` · ${activeSession.product_name}` : ''}</p>
                <p data-testid="time-tracker-elapsed" className={cn('font-mono text-5xl font-bold tabular-nums', isPaused && 'opacity-50')}>{fmtElapsed(elapsed)}</p>
                <p className="text-xs text-muted-foreground">Active time only — paused minutes never count toward hours, streaks or consistency.</p>
                <Textarea data-testid="time-tracker-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Session notes (added on clock-out)..." rows={2} className="max-w-md" />
                <div className="flex flex-wrap justify-center gap-2">
                  <Button data-testid="time-tracker-pause-resume-button" size="lg" variant="outline" onClick={handlePauseResume} disabled={busy}
                    className={cn('gap-2', isPaused ? 'timer-running' : 'timer-paused')}>
                    {isPaused ? <><Play className="h-4 w-4" /> Resume</> : <><Pause className="h-4 w-4" /> Pause</>}
                  </Button>
                  <Button data-testid="time-tracker-clock-out-button" size="lg" variant="destructive" onClick={handleClockOut} disabled={busy} className="gap-2">
                    <Square className="h-4 w-4" /> Clock Out
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm font-semibold">Start a session</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <p className="mb-1 text-xs text-muted-foreground">Category</p>
                    <Select value={category} onValueChange={setCategory}>
                      <SelectTrigger data-testid="time-tracker-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{(stagesData.time_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-muted-foreground">Product (optional)</p>
                    <Select value={productId} onValueChange={setProductId}>
                      <SelectTrigger data-testid="time-tracker-product-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No product</SelectItem>
                        {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Textarea data-testid="time-tracker-start-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What are you working on?" rows={2} />
                <Button data-testid="time-tracker-clock-in-button" size="lg" onClick={handleClockIn} disabled={busy} className="accent-btn w-full gap-2 sm:w-auto">
                  <Play className="h-4 w-4" /> Clock In
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-1">
          <KPICard testId="time-kpi-today" label="Today" value={minsToHM(summary?.today_minutes || 0)} />
          <KPICard testId="time-kpi-streak" label="Streak" value={`${summary?.streak || 0} days`} />
          <KPICard testId="time-kpi-consistency" label="Consistency Score" value={`${summary?.consistency_score ?? 0}/100`} sub="days worked + avg hours, last 7d" />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardContent className="p-4">
            <SectionTitle>Hours per day (last 7)</SectionTitle>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="label" tick={AXIS_TICK} />
                  <YAxis tick={AXIS_TICK} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Bar dataKey="hours" name="Hours" fill="hsl(var(--chart-3))" radius={BAR_RADIUS} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <SectionTitle right={
              <Button data-testid="time-ai-feedback-button" size="sm" variant="outline" onClick={getFeedback} disabled={fbBusy} className="gap-1.5">
                {fbBusy ? <Spinner /> : <Sparkles className="h-3.5 w-3.5" />} AI Feedback
              </Button>
            }>This week by category</SectionTitle>
            {summary && categoryWeek.length > 0 ? (
              <div className="space-y-1.5">
                {categoryWeek.map(([cat, mins]) => (
                  <div key={cat} className="flex items-center justify-between text-sm">
                    <span>{cat}</span>
                    <span className="font-mono tabular-nums text-muted-foreground">{minsToHM(mins)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Nothing logged this week yet.</p>
            )}
            {feedback && (
              <div data-testid="time-ai-feedback-card" className="mt-3 rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm">
                <p>{feedback.assessment}</p>
                <p className="mt-1"><span className="font-semibold">Biggest issue:</span> {feedback.biggest_issue}</p>
                <ul className="mt-1 list-disc pl-4 text-xs">{(feedback.recommendations || []).map((r) => <li key={r}>{r}</li>)}</ul>
                <p className="mt-1 text-xs text-muted-foreground">Ideal split: {feedback.ideal_split}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div>
        <SectionTitle>Recent sessions</SectionTitle>
        {sessions.length === 0 ? (
          <EmptyState testId="time-sessions-empty" icon={Clock} title="No sessions yet" subtitle="Clock in above to start tracking. Your streak and consistency score build automatically." />
        ) : (
          <Card>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Product</TableHead>
                <TableHead className="text-right">Duration</TableHead><TableHead>Notes</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {sessions.map((s) => (
                  <TableRow key={s.id} data-testid="time-session-row">
                    <TableCell className="font-mono text-sm tabular-nums">{s.date}</TableCell>
                    <TableCell className="text-sm">{s.category}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{s.product_name || '—'}</TableCell>
                    <TableCell className="text-right font-mono text-sm tabular-nums">{s.duration_minutes ? minsToHM(s.duration_minutes) : <span className="text-emerald-500">active</span>}</TableCell>
                    <TableCell className="max-w-[240px] truncate text-xs text-muted-foreground">{s.notes || '—'}</TableCell>
                    <TableCell>
                      <Button data-testid="time-session-delete" variant="ghost" size="icon" aria-label="Delete session" onClick={() => delSession(s)}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
    </div>
  );
}
